import { CalendarClock, CheckCircle2, FileText, Pencil, Send, Trash2, X } from 'lucide-react';
import { MoneyAmount } from '@/components/design-system';
import { StatutPaiementBadge, isPaiementEnRetard } from '@/components/finances/StatutPaiementBadge';
import { useRelances } from '@/hooks/queries/useRelances';
import { usePlansPaiement } from '@/hooks/queries/usePlansPaiement';
import type { FactureRecouvrementRow } from '@/hooks/queries/useRecouvrementEntreprises';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';

interface Props {
  facture: FactureRecouvrementRow | null;
  onClose: () => void;
  onEnregistrerEncaissement: (f: FactureRecouvrementRow) => void;
  onGeneratePdf: (f: FactureRecouvrementRow) => void;
  onEdit: (f: FactureRecouvrementRow) => void;
  onDelete: (f: FactureRecouvrementRow) => void;
  onRelancer?: (f: FactureRecouvrementRow) => void;
  onCreerPlan?: (f: FactureRecouvrementRow) => void;
}

// Mapping recouvrement → ConsolideStatut
function mapStatut(s: 'a_encaisser' | 'partiel' | 'regle'): 'a_payer' | 'partiel' | 'paye' {
  if (s === 'a_encaisser') return 'a_payer';
  if (s === 'regle') return 'paye';
  return 'partiel';
}

export function FactureRecouvrementDrawer(props: Props): JSX.Element {
  if (!props.facture) return <></>;
  return <DrawerInner {...props} facture={props.facture} />;
}

function DrawerInner({
  facture,
  onClose,
  onEnregistrerEncaissement,
  onGeneratePdf,
  onEdit,
  onDelete,
  onRelancer,
  onCreerPlan,
}: Props & { facture: FactureRecouvrementRow }): JSX.Element {
  const { data: relances = [] } = useRelances({ recouvrementId: facture.id });
  const { data: plans = [] } = usePlansPaiement({ recouvrementId: facture.id });
  const reste = facture.montant_ttc - facture.montant_encaisse;
  const enRetard = isPaiementEnRetard(mapStatut(facture.statut), facture.date_formation);
  const statutLabel = {
    a_encaisser: 'À encaisser',
    partiel: 'Partiel',
    regle: 'Réglé',
  } as const;
  const canRelance = facture.statut !== 'regle' && !!facture.tier_id;
  const canPlan = facture.statut !== 'regle' && !!facture.tier_id && plans.length === 0;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl sm:max-w-lg"
        role="dialog"
        aria-label="Détail facture"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Facture B2B
            </div>
            <h2 className="mt-1 truncate text-lg font-bold text-slate-900">
              {facture.entreprise_cliente_nom}
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <StatutPaiementBadge statut={mapStatut(facture.statut)} enRetard={enRetard} />
              <span className="font-mono text-xs text-slate-500">{facture.reference_facture}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(facture)}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Modifier"
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(facture)}
              className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
              aria-label="Supprimer"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">
                TTC facturé
              </div>
              <div className="mt-1">
                <MoneyAmount amount={facture.montant_ttc} variant="kpi" />
              </div>
              <div className="mt-1 text-[11px] text-slate-600">
                HT {facture.montant_ht.toLocaleString('fr-DZ')} · TVA{' '}
                {facture.montant_tva.toLocaleString('fr-DZ')}
              </div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                Reste à encaisser
              </div>
              <div className="mt-1">
                <MoneyAmount amount={reste} variant="kpi" />
              </div>
              {facture.montant_encaisse > 0 && (
                <div className="mt-1 text-[11px] text-slate-600">
                  Déjà encaissé : {facture.montant_encaisse.toLocaleString('fr-DZ')} DA
                </div>
              )}
            </div>
          </div>

          <Section title="Prestation">
            <Field label="Année" value={String(facture.annee)} />
            <Field label="Formation" value={facture.formation_titre ?? '—'} />
            <Field label="Date" value={facture.date_formation ?? '—'} />
            <Field label="Lieu" value={facture.lieu ?? '—'} />
            <Field
              label="Participants"
              value={facture.participants > 0 ? String(facture.participants) : '—'}
            />
          </Section>

          {facture.montant_encaisse > 0 && (
            <Section title="Dernier encaissement">
              <Field label="Date" value={facture.date_reglement ?? '—'} />
              <Field label="Mode" value={facture.mode_reglement ?? '—'} />
              <Field label="Référence" value={facture.reference_virement ?? '—'} mono />
              <Field label="Statut" value={statutLabel[facture.statut]} />
              {facture.operation_id && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Lié à l&apos;opération{' '}
                  <span className="font-mono">{facture.operation_id.slice(0, 8)}…</span>
                </div>
              )}
            </Section>
          )}

          {facture.facture_pdf_url && (
            <Section title="Facture PDF">
              <span className="font-mono text-xs text-slate-600">{facture.facture_pdf_url}</span>
              <p className="mt-1 text-[11px] text-slate-500">
                Régénérez via le bouton ci-dessous pour obtenir un nouveau lien signé (10 min).
              </p>
            </Section>
          )}

          {facture.observation && (
            <Section title="Observation">
              <p className="text-sm text-slate-700">{facture.observation}</p>
            </Section>
          )}

          {plans.length > 0 && (
            <Section title="Plan de paiement">
              {plans.map((p) => (
                <div
                  key={p.id}
                  className="rounded-md border border-violet-200 bg-violet-50/60 px-2.5 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-violet-900">{p.reference}</span>
                    <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                      {p.statut}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600">
                    {p.nb_echeances} tranches · {formatDA(Number(p.montant_total))} · {p.frequence}
                  </div>
                </div>
              ))}
            </Section>
          )}

          <Section title={`Relances (${relances.length})`}>
            {relances.length === 0 ? (
              <p className="text-xs text-slate-500">Aucune relance envoyée pour cette facture.</p>
            ) : (
              <ul className="space-y-1.5">
                {relances.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-700">
                        {t(`echeances.relances.niveau.${r.niveau}`)}{' '}
                        <span className="font-normal text-slate-500">
                          · {t(`echeances.relances.canal.${r.canal}`)}
                        </span>
                      </span>
                      <span className="font-mono text-[10px] text-slate-500">
                        {r.sent_at ? formatDateDZ(r.sent_at.slice(0, 10)) : '—'}
                      </span>
                    </div>
                    {r.destinataire && (
                      <div className="mt-0.5 text-[11px] text-slate-500">→ {r.destinataire}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
        <footer className="space-y-2 border-t border-slate-200 p-4">
          {facture.statut !== 'regle' && (
            <button
              type="button"
              onClick={() => onEnregistrerEncaissement(facture)}
              className={cn(
                'inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              )}
            >
              <CheckCircle2 className="h-4 w-4" />
              {facture.statut === 'partiel'
                ? 'Enregistrer un encaissement'
                : 'Encaisser la facture'}
            </button>
          )}
          {canRelance && onRelancer && (
            <button
              type="button"
              onClick={() => onRelancer(facture)}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-amber-700"
            >
              <Send className="h-4 w-4" /> Relancer le client
            </button>
          )}
          {canPlan && onCreerPlan && (
            <button
              type="button"
              onClick={() => onCreerPlan(facture)}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-4 text-sm font-semibold text-violet-800 shadow-sm transition-all hover:bg-violet-100"
            >
              <CalendarClock className="h-4 w-4" /> Échelonner en plan de paiement
            </button>
          )}
          <button
            type="button"
            onClick={() => onGeneratePdf(facture)}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
          >
            <FileText className="h-4 w-4" /> Générer la facture PDF
          </button>
        </footer>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cn('text-right font-medium text-slate-800', mono && 'font-mono text-xs')}>
        {value}
      </span>
    </div>
  );
}
